// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import EntitySelection from '../../../entities/EntitySelection';
import System from '../../../entities/systems/System';
import SystemBus from '../../../entities/SystemBus';
import Transform from '../../../math/Transform';
import Material from '../../../renderer/Material';
import ShaderLib from '../../../renderer/shaders/ShaderLib';
import Sphere from '../../../shapes/Sphere';
import Box from '../../../shapes/Box';
import Cylinder from '../../../shapes/Cylinder';
import Vector3 from '../../../math/Vector3';

/**
 * Renders all ParticleSystemComponents in the scene.
 */
class ParticleDebugRenderSystem extends System {
  constructor() {
    super('ParticleDebugRenderSystem', ['ParticleSystemComponent']);

    this.priority = System.PRIORITY_AFTER_DEFAULT;

    this.renderList = [];
    this.camera = null;

    SystemBus.addListener('sumerian.setCurrentCamera', (newCam) => {
      this.camera = newCam.camera;
    });

    /**
     * If set to true, all entities with a ParticleSystemComponent attached is rendered, and the selection is disregarded.
     * @type {boolean}
     */
    this.renderAll = true;

    /**
     * The selected entities to be rendered.
     * @type {EntitySelection}
     */
    this.selection = new EntitySelection();

    const material = new Material(ShaderLib.simpleColored);
    material.uniforms.color = [0, 1, 0];
    this.sphereRenderable = {
      materials: [material],
      transform: new Transform(),
      meshData: new Sphere(12, 12, 1),
    };
    this.boxRenderable = {
      materials: [material],
      transform: new Transform(),
      meshData: new Box(1, 1, 1),
    };
    this.coneRenderable = {
      materials: [material],
      transform: new Transform(),
      meshData: new Cylinder(16, 1, 1, 1),
    };
  }

  /**
   * @private
   * @param  {array} entities
   */
  process(entities) {
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      const meshEntity = entity.particleSystemComponent.meshEntity;
      if (meshEntity) {
        if (entity.isVisiblyHidden()) {
          meshEntity.meshRendererComponent.hidden = true;
        } else {
          meshEntity.meshRendererComponent.hidden = !this._shouldRenderDebugForEntity(entity);
        }
      }
    }
  }

  _shouldRenderDebugForEntity(entity) {
    return !this.passive && (this.renderAll || this.selection.contains(entity));
  }

  /**
   * @private
   * @param  {Renderer} renderer
   */
  render(renderer) {
    if (!this.camera || this.passive) {
      return;
    }

    renderer.checkResize(this.camera);

    const entities = this._activeEntities;
    for (let i = 0, N = entities.length; i !== N; i++) {
      const entity = entities[i];

      if (!this.renderAll && !this.selection.contains(entity)) {
        // Render selection is enabled, but this entity is not a part of it
        continue;
      }

      let renderable;
      switch (entity.particleSystemComponent.shapeType) {
        case 'sphere':
          renderable = this.sphereRenderable;
          const radius = entity.particleSystemComponent.sphereRadius;
          renderable.transform.setIdentity();
          renderable.transform.scale.setDirect(radius, radius, radius);
          break;
        case 'box':
          renderable = this.boxRenderable;
          renderable.transform.setIdentity();
          renderable.transform.scale.copy(entity.particleSystemComponent.boxExtents);
          break;
        case 'cone':
          const coneRadius = entity.particleSystemComponent.coneRadius;
          renderable = this.coneRenderable;
          renderable.transform.setIdentity();
          renderable.transform.rotation.fromAngles(3 * Math.PI / 2, 0, 0);
          renderable.transform.translation.set(0, entity.particleSystemComponent.coneLength * 0.5, 0);

          renderable.meshData.radiusTop = coneRadius + Math.tan(entity.particleSystemComponent.coneAngle) * entity.particleSystemComponent.coneLength;
          renderable.meshData.radiusBottom = coneRadius;
          renderable.meshData.height = entity.particleSystemComponent.coneLength;
          renderable.meshData.rebuild();
          renderable.meshData.setVertexDataUpdated();
          break;
      }

      if (renderable) {
        renderable.meshData.indexModes = ['Lines'];
        const transform = renderable.transform.update();

        const worldTransform = entity.transformComponent.sync().worldTransform;
        tmpTransform.setIdentity();
        tmpTransform.translation.set(worldTransform.translation);
        tmpTransform.rotation.copy(worldTransform.rotation);

        transform.multiply(tmpTransform.update(), transform);

        transform.update();
        if(renderer.currentViewer){
          renderer.render(renderable, null, renderer.currentViewer, null, null, false);
        }
      }
    }
  }

  /**
   * @private
   */
  cleanup() {}

  /**
   * @private
   */
  update() {
    if (this.passive) {
      return;
    }

    const entities = this._activeEntities;
    let l = entities.length;
    while (l--) {
      const entity = entities[l];

      if (this._shouldRenderDebugForEntity(entity)) {
        entity.particleSystemComponent.play();
      } else {
        entity.particleSystemComponent.stop();
      }
    }

    this.process(this._activeEntities);
  }
}

const tmpTransform = new Transform();
export default ParticleDebugRenderSystem;
